import { useState, useCallback, useRef, useEffect } from 'react'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import type { FormNode } from '../../../../types/form.types'
import { useFormChrome } from '../formChromeTheme'

export interface LabelElementProps {
  node: FormNode
  /** When set, double-click enables inline editing (editor mode). */
  onTitleChange?: (title: string) => void
  preview?: boolean
}

export function LabelElement({ node, onTitleChange, preview }: LabelElementProps) {
  const chrome = useFormChrome()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(node.title || node.name)
  const inputRef = useRef<HTMLInputElement>(null)

  const canEdit = !preview && onTitleChange

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const commit = useCallback(() => {
    if (!onTitleChange) return
    const v = value.trim() || node.name
    onTitleChange(v)
    setValue(v)
    setEditing(false)
  }, [onTitleChange, value, node.name])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        commit()
      }
      if (e.key === 'Escape') {
        setValue(node.title || node.name)
        setEditing(false)
        inputRef.current?.blur()
      }
    },
    [commit, node.title, node.name],
  )

  if (editing && canEdit) {
    return (
      <TextField
        inputRef={inputRef}
        size="small"
        fullWidth
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        variant="standard"
        sx={{ minWidth: 60, '& .MuiInput-root': { fontSize: 13, fontWeight: 500 } }}
      />
    )
  }

  const style = node.style ?? {}
  const mutedSub = style.labelSubline === 'muted'
  const journalSec = style.labelPreset === 'journalSection'

  const label = (
    <Typography
      variant="body2"
      sx={{
        color: journalSec ? chrome.title : mutedSub ? chrome.muted : 'text.primary',
        cursor: canEdit ? 'text' : 'default',
        minHeight: journalSec ? 20 : 24,
        display: 'flex',
        alignItems: 'center',
        fontSize: journalSec ? 11 : mutedSub ? 12 : style.fontSize ?? 13,
        fontWeight: journalSec ? 600 : style.bold ? 700 : 500,
        fontStyle: mutedSub || journalSec ? 'normal' : style.italic ? 'italic' : 'normal',
        textAlign: style.textAlign,
        lineHeight: journalSec ? 1.35 : mutedSub ? 1.45 : undefined,
        fontFamily: journalSec ? chrome.monoFont : mutedSub ? chrome.uiFont : undefined,
        mb: journalSec ? 1 : mutedSub ? 0.5 : 0,
      }}
      onDoubleClick={canEdit ? (e) => { e.stopPropagation(); setEditing(true) } : undefined}
    >
      {node.title || node.name}
    </Typography>
  )

  if (node.tooltip) {
    return <Tooltip title={node.tooltip}>{label}</Tooltip>
  }
  return label
}
